/**
 * Shared Sentry log sampling/filter config and logic.
 * No @sentry/nextjs import — safe to use from all Sentry configs (client, server, edge).
 */
import type { BeforeSendLogCallback, BeforeSendLogOptions, SentryLogLike } from './types';

/** Info-level logs with these categories are always sent; other info logs are sampled. */
export const INFO_CATEGORIES_ALWAYS_SEND = new Set<string>(['integration_health']);

/** Sample rate for info-level logs that are not in INFO_CATEGORIES_ALWAYS_SEND (0–1). */
export const INFO_SAMPLE_RATE = 0.1;

/**
 * Returns a beforeSendLog-style filter. Pass env-derived isDebugLogsEnabled from each runtime.
 * - warn/error/fatal: always send
 * - debug: send only if isDebugLogsEnabled
 * - info: send if category is in INFO_CATEGORIES_ALWAYS_SEND, else sample at INFO_SAMPLE_RATE
 * - other: drop
 */
export function createBeforeSendLog(options: BeforeSendLogOptions): BeforeSendLogCallback {
	const { isDebugLogsEnabled } = options;

	/** Returns true with probability INFO_SAMPLE_RATE; used to sample info logs not in the allowlist. */
	function shouldSendSampledInfoLog(): boolean {
		return Math.random() < INFO_SAMPLE_RATE;
	}

	function beforeSendLog(log: unknown): SentryLogLike | null {
		// Guard: Sentry may call with null, undefined, or non-object; avoid throwing (see Sentry finding: beforeSendLog can throw on non-object).
		if (log === null || typeof log !== 'object') return null;
		const logObj = log as SentryLogLike;
		const level = String(logObj.level ?? '');
		switch (level) {
			case 'warn':
			case 'error':
			case 'fatal':
				return logObj;
			case 'debug':
				return isDebugLogsEnabled ? logObj : null;
			case 'info': {
				const category = logObj.attributes?.category;
				if (!!category && INFO_CATEGORIES_ALWAYS_SEND.has(String(category))) return logObj;
				return shouldSendSampledInfoLog() ? logObj : null;
			}
			default:
				return null;
		}
	}
	return beforeSendLog;
}
